import { tw } from "@/utils";
import CheckIcon from "./check.svg?react";
import CancelIcon from "./cancel.svg?react";
import {
  defaultPatientStatus,
  PatientStatusInfo,
} from "@/components/PatientInfo/PatientInfoCard/PatientStatusInfo";
import { MedicalHistoryInfo } from "@/components/PatientInfo/PatientInfoCard/MedicalHistoryInfo";
import { ConditionalWrapper } from "@/components/ConditionalWrapper/ConditionalWrapper";
import { BranchIcon } from "./BranchIcon";
import { useWindowStore } from "@/store/WindowWidth";
import { useGetPatientStatus } from "@/components/PatientInfo/PatientInfoCard/api";
import { useParams } from "react-router";
import { getAgeFromBirthDate } from "@/components/PatientInfo/PatientInfoCard/utils";
import dayjs from "dayjs";
import { useGetTransferInfo } from "../CareInsight/CareInsightDetail/api";

interface PatientDetailProps {
  // props 정의
}

export const PatientDetail = (props: PatientDetailProps) => {
  const {} = props;
  const params = useParams();

  if (!params.requestId) throw new Error();

  const { data, isSuccess, isLoading, isError } = useGetPatientStatus(
    params.requestId
  );
  const {
    data: transferData,
    isSuccess: transferIsSuccess,
    isLoading: transferIsLoading,
    isError: transferIsError,
  } = useGetTransferInfo(params.requestId);

  const width = useWindowStore((state) => state.width);
  const isResponse = width < 1600;

  return (
    <div className="w-full h-full bg-white rounded-[16px] shadow-patient-box">
      {/* header */}
      <div
        className={tw(
          "flex items-center w-full h-[72px] p-[20px]",
          "border-solid border-b-[1px] border-gray-g30"
        )}
      >
        {isSuccess && (
          <>
            <div className="text-[22px] font-semibold text-gray-g500">
              {data.result.data.patient.name}
            </div>
            <div className="text-base font-regular text-gtay-g200 ml-[10px]">
              {getAgeFromBirthDate(data.result.data.patient.birthDt)
                ? `만 ${getAgeFromBirthDate(data.result.data.patient.birthDt)}세`
                : "나이 정보 없음"}
            </div>
            <div
              className={tw(
                "ml-auto flex items-center justify-center w-[76px] h-[28px] rounded-[20px] bg-[#FF6D57]/8",
                "text-sm font-semibold text-[#FF6D57]",
                "border-solid border-b-[1px] border-gray-g30"
              )}
            >
              {data.result.data.transferType}
            </div>
          </>
        )}
      </div>
      {/* body */}
      <div
        className={tw(
          "flex w-full h-[calc(100%-72px)]",
          isResponse && "flex-col overflow-auto p-[20px] gap-[20px]"
        )}
      >
        <ConditionalWrapper
          shouldWrap={!isResponse}
          className={tw("w-1/2 h-full p-[20px] overflow-auto")}
        >
          <div className="flex flex-col gap-[14px]">
            <div className={tw(titleFontStyle)}>환자 기본 정보</div>
            <div className="flex flex-col gap-[18px]">
              <div className="flex items-center justify-between">
                <div className={tw(labelFontStyle)}>성별</div>
                <div className={tw(valueFontStyle)}>
                  {isSuccess && data.result.data.patient.gender === 0
                    ? "남"
                    : "여"}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className={tw(labelFontStyle)}>생년월일</div>
                {isSuccess && (
                  <div className={tw(valueFontStyle)}>
                    {getAgeFromBirthDate(data.result.data.patient.birthDt)
                      ? `${dayjs(data.result.data.patient.birthDt).format("YYYY-MM-DD")} / 만 ${getAgeFromBirthDate(data.result.data.patient.birthDt)}세`
                      : "나이 정보 없음"}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className={tw(labelFontStyle)}>보호자</div>
                {isSuccess && (
                  <div className={tw(valueFontStyle)}>
                    {`${data.result.data.patient.hasGd ? "유" : "무"}`}{" "}
                    {data.result.data.patient.hasGd &&
                      `/ ${data.result.data.patient.gdRelation}`}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="w-full h-[1px] bg-gray-g30 my-[20px]"></div>
          <div className="flex flex-col gap-[14px]">
            <div className={tw(titleFontStyle)}>환자 상태 평가</div>
            <PatientStatusInfo
              noOutline
              data={
                isSuccess
                  ? data?.result.data.status[0].patientStatus
                  : defaultPatientStatus
              }
            />
          </div>
          <div className="w-full h-[1px] bg-gray-g30 my-[20px]"></div>
          <div className="flex flex-col gap-[14px]">
            <div className={tw(titleFontStyle)}>진료이력</div>
            <MedicalHistoryInfo
              data={isSuccess ? data.result.data.medicalHistory : []}
            />
          </div>
        </ConditionalWrapper>
        <ConditionalWrapper
          shouldWrap={!isResponse}
          className={tw(
            "flex flex-col",
            "w-1/2 h-full p-[20px] overflow-auto",
            "border-solid border-l-[1px] border-gray-g30"
          )}
        >
          <div
            className={tw(
              "flex flex-col gap-[14px]",
              isResponse && "mt-[40px]"
            )}
          >
            <div className={tw(titleFontStyle)}>이송 정보</div>
            <div className="flex flex-col gap-[18px]">
              <div className="flex items-center justify-between">
                <div className={tw(labelFontStyle)}>이송기관</div>
                {transferIsSuccess && (
                  <div className={tw(valueFontStyle)}>
                    {transferData.result.data.managerName}
                    <span className="text-gray-g75">{" | "}</span>
                    {transferData.result.data.managerPhone
                      ? transferData.result.data.managerPhone
                      : "연락처 정보없음"}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className={tw(labelFontStyle)}>현재 이송차량 위치</div>
                {transferIsSuccess && (
                  <div className={tw(valueFontStyle)}>
                    {transferData.result.data.transferPosition}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className={tw(labelFontStyle)}>도착 잔여시간</div>
                {transferIsSuccess && (
                  <div className={tw(valueFontStyle)}>
                    {transferData.result.data.restTime
                      ? `${transferData.result.data.restTime}분`
                      : "정보없음"}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className={tw(labelFontStyle)}>남은 거리 (km)</div>
                {transferIsSuccess && (
                  <div className={tw(valueFontStyle)}>
                    {transferData.result.data.restDistance
                      ? `${transferData.result.data.restDistance}km`
                      : "정보없음"}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-[12px]">
                <div className={tw(labelFontStyle)}>이송 상태</div>
                <div className="relative h-[48px]">
                  <div className="relative flex items-center w-full h-[24px]">
                    <div
                      className="h-[3px] w-[calc(100%-30px)]"
                      style={{
                        backgroundImage:
                          "repeating-linear-gradient(to right, #ccc 0px, #ccc 2px, transparent 3px, transparent 6px)",
                      }}
                    />
                  </div>
                  <div className="absolute top-0 left-0">
                    <BranchIcon isReached />
                    <div className="text-sm font-medium text-gray-g300 mt-[8px]">
                      이송 시작
                    </div>
                  </div>
                  <div className="absolute top-0 right-[30%] flex flex-col items-center">
                    <BranchIcon />
                    <div className="text-sm font-medium text-gray-g300 mt-[8px]">
                      500m 이내
                    </div>
                  </div>
                  <div className="absolute top-0 right-0 flex flex-col items-center">
                    <BranchIcon />
                    <div className="text-sm font-medium text-gray-g300 mt-[8px]">
                      병원 도착
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={tw(
              "mt-auto w-full aspect-video bg-white p-[8px]",
              "border-solid border-[1px] border-gray-g30 rounded-[14px] "
            )}
          >
            <div className="relative w-full h-full bg-gray-g500 rounded-[10px]">
              <video className="w-full h-full" />
              <div className="absolute inset-0 flex items-center justify-center text-white">
                구급차 CCTV 영상
              </div>
              <div className="absolute top-[8px] right-[8px] flex items-center justify-center gap-[4px] w-[52px] h-[20px] bg-gray-g30 rounded-[20px]">
                <div className="text-xs font-regular text-gray-g300">Live</div>
                <div className="w-[5px] h-[5px] bg-red rounded-full animate-blink" />
              </div>
            </div>
          </div>
        </ConditionalWrapper>
      </div>
      {/* footer */}
      {/* <div
        className={tw(
          "flex items-center justify-center gap-[16px] w-full h-[84px] px-[20px]",
          "border-solid border-t-[1px] border-gray-g30"
        )}
      >
        <button
          className={tw(
            "flex items-center justify-center gap-[10px] stroke-[1.5px]",
            "w-[230px] h-[44px] bg-purple-100 rounded-[8px] shadow-dicision-button",
            "text-base font-semibold text-white"
          )}
        >
          <CheckIcon />
          환자 이송 완료
        </button>
        <button
          className={tw(
            "flex items-center justify-center gap-[10px]",
            " w-[230px] h-[44px] bg-white rounded-[8px] shadow-dicision-button",
            "border-solid border-[1px] border-gray-g75",
            "text-base font-semibold text-gray-g200"
          )}
        >
          <CancelIcon />
          이송 취소
        </button>
      </div> */}
    </div>
  );
};

const titleFontStyle = tw("text-lg font-medium text-gray-g400");
const labelFontStyle = tw("text-base font-medium text-gray-g100");
const valueFontStyle = tw("text-base font-semibold text-gray-g300");
